import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { MatButtonModule } from '@angular/material/button';
import { SupabaseUserToolsService } from 'src/app/shared/services/supabase-user-tools.service';
import { NotificationsService } from 'src/app/shared/services/notifications.service';
import {
  Notification,
  NotificationSeverity,
} from 'src/app/shared/models/notification.model';
import { MatTabsModule } from '@angular/material/tabs';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { NotificationSettingsComponent } from './notification-settings/notification-settings.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-notifications',
  imports: [
    DatePipe,
    TranslatePipe,
    MatButtonModule,
    CommonModule,
    CdkAccordionModule,
    MatTabsModule,
    MatExpansionModule,
    MatTableModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    NotificationSettingsComponent,
    MatDatepickerModule,
    MatTimepickerModule,
    MatIconModule,
  ],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit {
  notifications: Notification[] = [];

  /** Material icon of each severity (the chip; its colour comes from the row). */
  readonly severityIcons: Record<NotificationSeverity, string> = {
    info: 'info',
    success: 'check_circle',
    warning: 'warning',
    critical: 'error',
  };
  isAdmin = false;
  loading = false;

  // Admin form fields
  title!: string;
  message!: string;
  announcement_title!: string;
  announcement_body = '';

  startingDateTime = false;
  endingDateTime = false;
  startingDatepicker: Date | null = null;
  startingDate: Date | string | null = null;
  startingTimepicker: Date | null = null;
  startingTime: Date | string | null = null;

  // Server-side pagination
  pageSize = 10;
  currentPage = 1;
  totalCount = 0;

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  /** Unread on this page, or anywhere in the inbox (the badge count). */
  get hasUnread(): boolean {
    return this.unreadCount > 0 || this.notifications.some((n) => !n.read);
  }

  unreadCount = 0;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private supabaseUserToolsService: SupabaseUserToolsService,
    private notificationsService: NotificationsService,
    private snackBarService: SnackBarService,
    protected translationService: TranslationService
  ) {}

  async ngOnInit() {
    this.notificationsService.unreadCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => (this.unreadCount = count));
    await this.loadPage(1);
    this.isAdmin = await this.supabaseUserToolsService.isUserAdmin();
  }

  async loadPage(page: number): Promise<void> {
    this.loading = true;
    this.currentPage = page;
    const result = await this.notificationsService.getAllNotifications(
      page,
      this.pageSize
    );
    this.notifications = result.notifications;
    this.totalCount = result.totalCount;
    this.loading = false;
  }

  async nextPage(): Promise<void> {
    if (this.currentPage < this.totalPages) {
      await this.loadPage(this.currentPage + 1);
    }
  }

  async prevPage(): Promise<void> {
    if (this.currentPage > 1) {
      await this.loadPage(this.currentPage - 1);
    }
  }

  /**
   * Mark a single notification as read.
   * Optimistically updates the UI, then fires the RPC.
   */
  async markAsRead(notif: Notification): Promise<void> {
    if (notif.read) return;
    notif.read = true;
    await this.notificationsService.markNotificationAsRead(notif.id);
  }

  /**
   * Mark all notifications as read.
   * Optimistically updates the current page, the RPC marks the whole inbox.
   */
  async markAllAsRead(): Promise<void> {
    this.notifications.forEach((n) => (n.read = true));
    await this.notificationsService.markAllAsRead();
  }

  // ── Admin actions ─────────────────────────────────────

  async createNotificationByAdmin(form?: { resetForm(): void }) {
    const success = await this.notificationsService.createNotificationAdmin(
      this.title,
      this.message
    );

    if (success) {
      this.title = '';
      this.message = '';
      if (form) {
        form.resetForm();
      }
      this.snackBarService.openSnackBar('Notification sent successfully!');
    }
  }

  updateDateTime(changedValue: string) {
    if (changedValue === 'starting')
      this.startingDateTime = !this.startingDateTime;
    else if (changedValue === 'ending')
      this.endingDateTime = !this.endingDateTime;
  }

  async createAnnouncement(form?: { resetForm(): void }) {
    if (this.announcement_title == '' && this.announcement_body == '') return;
    const success = await this.notificationsService.createAnnouncement(
      this.announcement_title,
      this.announcement_body
    );

    if (success) {
      this.announcement_title = '';
      this.announcement_body = '';
      if (form) {
        form.resetForm();
      }
      this.snackBarService.openSnackBar('Announcement posted successfully!');
    }
  }
}
